<template>
    <node-view-wrapper as="div" class="doc-image" :class="[`doc-image--${align}`, { 'doc-image--resizing': !!drag }]" data-drag-handle>
        <div ref="frame" class="doc-image__frame" :style="frameStyle">
            <img :src="node.attrs.src" :alt="node.attrs.alt || ''" :title="node.attrs.title || null" draggable="false" />

            <template v-if="selected && editor.isEditable">
                <!-- Alcas de redimensionamento nos quatro cantos (mantem a proporcao). -->
                <span v-for="corner in corners" :key="corner" :class="`doc-image__handle doc-image__handle--${corner}`"
                    @mousedown.prevent.stop="startResize($event, corner)"></span>

                <!-- Barrinha com alinhamento e tamanho; mousedown e bloqueado para nao perder a selecao. -->
                <div class="doc-image__bar" contenteditable="false" @mousedown.prevent.stop>
                    <button v-for="option in alignments" :key="option.value" type="button" class="doc-image__btn"
                        :class="{ 'is-active': align === option.value }" :title="option.label" @click="setAlign(option.value)">
                        <v-icon size="16">{{ option.icon }}</v-icon>
                    </button>
                    <span class="doc-image__sep"></span>
                    <button type="button" class="doc-image__btn" title="Tamanho original" :disabled="!node.attrs.width" @click="updateAttributes({ width: null })">
                        <v-icon size="16">mdi-image-size-select-actual</v-icon>
                    </button>
                    <span class="doc-image__size">{{ sizeText }}</span>
                </div>
            </template>
        </div>
    </node-view-wrapper>
</template>

<script>
import { NodeViewWrapper, nodeViewProps } from "@tiptap/vue-2";
import { CM_TO_PX } from "./brand";
import { IMAGE_ALIGNMENTS } from "./extensions/nodes";

const MIN_WIDTH = 40;

// Imagem do documento: mostra a figura com a largura gravada (em px a 100% de
// zoom, escalada pela variavel --doc-zoom da folha) e, quando selecionada,
// alcas nos cantos para redimensionar e botoes de alinhamento.
export default {
    name: "ImageView",
    components: { NodeViewWrapper },
    props: nodeViewProps,
    data() {
        return {
            drag: null,
            dragWidth: null,
            corners: ["top-left", "top-right", "bottom-left", "bottom-right"],
            alignments: IMAGE_ALIGNMENTS.map((value) => ({
                value,
                icon: `mdi-format-align-${value}`,
                label: { left: "Alinhar à esquerda", center: "Centralizar", right: "Alinhar à direita" }[value],
            })),
        };
    },
    computed: {
        align() {
            return IMAGE_ALIGNMENTS.includes(this.node.attrs.align) ? this.node.attrs.align : "left";
        },
        currentWidth() {
            return this.dragWidth || this.node.attrs.width || null;
        },
        frameStyle() {
            if (!this.currentWidth) return {};
            return { width: `calc(${this.currentWidth}px * var(--doc-zoom, 1))` };
        },
        sizeText() {
            if (!this.currentWidth) return "Tamanho original";
            return `${(this.currentWidth / CM_TO_PX).toFixed(1).replace(".", ",")} cm`;
        },
    },
    beforeDestroy() {
        this.stopResize();
    },
    methods: {
        setAlign(align) {
            this.updateAttributes({ align });
        },
        zoomFactor() {
            const value = parseFloat(getComputedStyle(this.$el).getPropertyValue("--doc-zoom"));
            return value > 0 ? value : 1;
        },
        startResize(event, corner) {
            const zoom = this.zoomFactor();
            const startWidth = this.$refs.frame.getBoundingClientRect().width / zoom;
            const maxWidth = Math.max(MIN_WIDTH, Math.floor(this.$el.clientWidth / zoom));
            // Puxando a alca da direita a largura cresce com o mouse; na da
            // esquerda, cresce quando o mouse vai para a esquerda. Centralizada,
            // a imagem cresce para os dois lados, entao a alca acompanha o dobro.
            const direction = corner.endsWith("right") ? 1 : -1;
            const factor = this.align === "center" ? 2 : 1;

            this.drag = {
                startX: event.clientX,
                move: (e) => {
                    const delta = ((e.clientX - this.drag.startX) * direction * factor) / zoom;
                    this.dragWidth = Math.round(Math.min(maxWidth, Math.max(MIN_WIDTH, startWidth + delta)));
                },
                stop: () => {
                    const width = this.dragWidth;
                    this.stopResize();
                    if (width) this.updateAttributes({ width });
                },
            };
            window.addEventListener("mousemove", this.drag.move);
            window.addEventListener("mouseup", this.drag.stop);
        },
        stopResize() {
            if (!this.drag) return;
            window.removeEventListener("mousemove", this.drag.move);
            window.removeEventListener("mouseup", this.drag.stop);
            this.drag = null;
            this.dragWidth = null;
        },
    },
};
</script>
